import { Modal } from '@mantine/core';
import { useMounted } from '@mantine/hooks';
import { useUnit } from 'effector-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from 'shared/lib';
import { Button, Lighting } from 'shared/ui';
import { calculatorBetModel } from '../../model';
import { CalculatorBetFastBets } from '../calculator-bet-fast-bets';
import { CalculatorBetKeyboard } from '../calculator-bet-keyboard';
import { CalculatorBetWagerField } from '../calculator-bet-wager-field';
import cls from './calculator-bet.module.scss';

type CalculatorBetProps = {
	targetId: string;
};

export const CalculatorBet = ({ targetId }: CalculatorBetProps) => {
	const { t } = useTranslation();
	const { active, disable } = useUnit(calculatorBetModel.switcher);
	const sm = useBreakpoint('sm', false);
	const mounted = useMounted();
	const contentRef = useRef<HTMLDivElement | null>(null);

	const [submit, enableOnboarding] = useUnit([
		calculatorBetModel.calculatorBetFormSubmitted,
		calculatorBetModel.$enableOnboardingCalculator,
	]);

	useEffect(() => {
		if (!(active || enableOnboarding)) return;

		const update = () => {
			const el = contentRef.current;
			if (!el) return;

			const rect = el.getBoundingClientRect();

			document.documentElement.style.setProperty(
				'--calculator-bet-content-top',
				`${rect.height}px`,
			);
		};

		requestAnimationFrame(update);

		const el = contentRef.current;
		if (!el) return;

		const ro = new ResizeObserver(update);
		ro.observe(el);

		window.addEventListener('resize', update);

		(() => {
			ro.disconnect();
			window.removeEventListener('resize', update);
		})();
	}, [active, enableOnboarding]);

	return (
		mounted &&
		!sm && (
			<Modal.Root
				variant='secondary'
				opened={active || enableOnboarding}
				zIndex={1500}
				className={cls.calculatorBet}
				classNames={{
					root: cls.calculatorBet,
					inner: cls.calculatorBetInner,
					body: cls.calculatorBetBody,
					content: cls.calculatorBetContent,
				}}
				closeOnClickOutside={!enableOnboarding}
				closeOnEscape={!enableOnboarding}
				portalProps={{ target: document.getElementById(targetId)! }}
				onClose={disable}
			>
				<Modal.Overlay />
				<Modal.Content
					ref={contentRef}
					data-onboarding={enableOnboarding || undefined}
				>
					<Lighting enabled={enableOnboarding}>
						<Modal.Header>
							<Modal.CloseButton />
							<Modal.Title>{t('calculator-bet.bet')}</Modal.Title>
						</Modal.Header>
						<Modal.Body>
							<form
								className={cls.calculatorBetForm}
								onSubmit={(event) => {
									event.preventDefault();
									submit();
								}}
							>
								<CalculatorBetWagerField />
								<CalculatorBetKeyboard />
								<CalculatorBetFastBets />
								<Button
									fullWidth
									className={cls.calculatorBetApply}
									type='submit'
								>
									{t('calculator-bet.apply')}
								</Button>
							</form>
						</Modal.Body>
					</Lighting>
				</Modal.Content>
			</Modal.Root>
		)
	);
};
